import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  adminListAll,
  adminToggleVisibility,
  adminDeletePerfume,
  adminCreatePerfume,
} from "../lib/api";
import { labels } from "../i18n/translations";
import ImagePlaceholder from "../components/ImagePlaceholder";
import { Eye, EyeOff, Trash2, Pencil, LogOut, ExternalLink, Plus } from "lucide-react";

const Row = ({ p, onToggle, onDelete, onEdit }) => {
  const displayName = p.name || `Profumo ${p.slot_number}`;
  return (
    <div
      data-testid={`admin-row-${p.slot_number}`}
      className="flex items-center gap-4 py-4 border-b border-black/10"
    >
      <div className="w-16 h-20 shrink-0 bg-[#F4F4F4] overflow-hidden">
        {p.main_image ? (
          <img src={p.main_image} alt="" className="w-full h-full object-cover" />
        ) : (
          <ImagePlaceholder label="" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="uppercase tracking-[0.22em] text-[10px] text-[#C5A059] truncate">
          {p.brand || "—"}
        </div>
        <div className="font-heading text-lg md:text-xl tracking-tight truncate">
          #{p.slot_number} · {displayName}
        </div>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[10px] uppercase tracking-[0.22em] text-black/40">
          {p.price && <span>{p.price}</span>}
          {p.size_ml && <span>{p.size_ml}</span>}
          {p.gender && <span>{p.gender}</span>}
          {p.availability && <span>{p.availability}</span>}
          <span className={p.visible ? "text-[#C5A059]" : "text-black/30"}>
            {p.visible ? labels.admin.visible_label : labels.admin.hidden_label}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={() => onToggle(p)}
          data-testid={`admin-toggle-${p.slot_number}`}
          title={p.visible ? labels.admin.hide : labels.admin.show}
          className="p-2 hover:text-[#C5A059] transition-colors"
        >
          {p.visible ? <Eye strokeWidth={1.25} className="w-5 h-5" /> : <EyeOff strokeWidth={1.25} className="w-5 h-5" />}
        </button>
        <button
          type="button"
          onClick={() => onEdit(p)}
          data-testid={`admin-edit-${p.slot_number}`}
          title={labels.admin.edit}
          className="p-2 hover:text-[#C5A059] transition-colors"
        >
          <Pencil strokeWidth={1.25} className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(p)}
          data-testid={`admin-delete-${p.slot_number}`}
          title={labels.admin.delete}
          className="p-2 hover:text-red-600 transition-colors"
        >
          <Trash2 strokeWidth={1.25} className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export const AdminDashboard = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [perfumes, setPerfumes] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoadingList(true);
    try {
      const data = await adminListAll();
      setPerfumes(data);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (user && user !== false) load();
  }, [user]);

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="uppercase tracking-[0.32em] text-[11px] text-black/40">…</div>
      </main>
    );
  }
  if (!user || user === false) return <Navigate to="/admin/login" replace />;

  const onToggle = async (p) => {
    await adminToggleVisibility(p.id, !p.visible);
    setPerfumes((prev) =>
      prev.map((x) => (x.id === p.id ? { ...x, visible: !p.visible } : x)),
    );
  };

  const onDelete = async (p) => {
    if (!window.confirm(labels.admin.confirm_delete)) return;
    await adminDeletePerfume(p.id);
    setPerfumes((prev) => prev.filter((x) => x.id !== p.id));
  };

  const onEdit = (p) => navigate(`/admin/profumo/${p.id}`);

  const onCreate = async () => {
    const newP = await adminCreatePerfume({ name: `Profumo ${perfumes.length + 1}`, visible: false });
    setPerfumes((prev) => [...prev, newP]);
    navigate(`/admin/profumo/${newP.id}`);
  };

  const filtered = perfumes.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      String(p.slot_number).includes(q) ||
      (p.brand || "").toLowerCase().includes(q) ||
      (p.name || "").toLowerCase().includes(q)
    );
  });

  return (
    <main data-testid="page-admin-dashboard" className="min-h-screen bg-white">
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-black/5 px-6 md:px-12 lg:px-20 py-5 flex items-center justify-between gap-4">
        <div>
          <div className="font-heading text-xl md:text-2xl tracking-tight">
            {labels.admin.title}
          </div>
          <div className="uppercase tracking-[0.32em] text-[10px] text-[#C5A059]">
            {labels.admin.subtitle}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/"
            target="_blank"
            data-testid="admin-open-public"
            className="hidden md:inline-flex items-center gap-2 uppercase tracking-[0.22em] text-[10px] border border-black/20 px-3 py-2 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
          >
            <ExternalLink strokeWidth={1.25} className="w-4 h-4" />
            {labels.admin.open_public}
          </Link>
          <button
            type="button"
            onClick={async () => {
              await logout();
              navigate("/admin/login");
            }}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 uppercase tracking-[0.22em] text-[10px] border border-black/20 px-3 py-2 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
          >
            <LogOut strokeWidth={1.25} className="w-4 h-4" />
            {labels.admin.logout}
          </button>
        </div>
      </div>

      <section className="px-6 md:px-12 lg:px-20 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="font-heading text-3xl md:text-4xl tracking-tight">
            {labels.admin.list_title.replace("{count}", perfumes.length)}
          </div>
          <button
            type="button"
            onClick={onCreate}
            data-testid="admin-create-new"
            className="inline-flex items-center gap-2 bg-black text-white hover:bg-[#C5A059] transition-colors uppercase tracking-[0.22em] text-[10px] py-3 px-5"
          >
            <Plus strokeWidth={1.25} className="w-4 h-4" />
            {labels.admin.new_perfume}
          </button>
        </div>

        <div className="mb-6">
          <input
            type="text"
            placeholder="Cerca per numero, marca o nome…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="admin-search"
            className="w-full border-0 border-b border-black/20 bg-transparent py-3 focus:outline-none focus:border-black text-sm"
          />
        </div>

        {loadingList ? (
          <div className="py-12 text-center uppercase tracking-[0.32em] text-[10px] text-black/40">…</div>
        ) : (
          <div data-testid="admin-list">
            {filtered.map((p) => (
              <Row key={p.id} p={p} onToggle={onToggle} onDelete={onDelete} onEdit={onEdit} />
            ))}
            {filtered.length === 0 && (
              <div className="py-12 text-center text-sm text-black/40">Nessun risultato.</div>
            )}
          </div>
        )}
      </section>
    </main>
  );
};

export default AdminDashboard;
